import { v4 as uuid } from "uuid";
import type { SemanticMetadata, SemanticType } from "../types/SemanticTypes";

const PROPERTY_NAME = "libertatem.semantic.index";
const TAG_PREFIX = "libertatem:";

export class MetadataService {
  create(type: SemanticType, partial: Partial<SemanticMetadata> = {}): SemanticMetadata {
    const now = new Date().toISOString();
    return {
      type,
      id: partial.id ?? `${type}-${uuid()}`,
      createdAt: partial.createdAt ?? now,
      updatedAt: now,
      ...partial
    };
  }

  encode(metadata: SemanticMetadata): string {
    return `${TAG_PREFIX}${JSON.stringify(metadata)}`;
  }

  decode(tag?: string): SemanticMetadata | null {
    if (!tag?.startsWith(TAG_PREFIX)) return null;
    try {
      return JSON.parse(tag.slice(TAG_PREFIX.length)) as SemanticMetadata;
    } catch {
      return null;
    }
  }

  async upsertDocumentIndex(context: Word.RequestContext, metadata: SemanticMetadata): Promise<void> {
    await this.upsertDocumentIndexMany(context, [metadata]);
  }

  async upsertDocumentIndexMany(context: Word.RequestContext, metadataList: SemanticMetadata[]): Promise<void> {
    if (metadataList.length === 0) return;
    const props = context.document.properties.customProperties;
    props.load("items/key,value");
    await context.sync();

    const existing = props.items.find((item) => item.key === PROPERTY_NAME);
    const current = existing ? this.parseIndex(existing.value) : [];
    const next = [...current.filter((item) => !metadataList.some((metadata) => metadata.id === item.id)), ...metadataList];

    if (existing) {
      existing.value = JSON.stringify(next);
    } else {
      props.add(PROPERTY_NAME, JSON.stringify(next));
    }
  }

  async removeFromDocumentIndex(context: Word.RequestContext, ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const props = context.document.properties.customProperties;
    props.load("items/key,value");
    await context.sync();
    const existing = props.items.find((item) => item.key === PROPERTY_NAME);
    if (!existing) return;
    const remove = new Set(ids);
    const next = this.parseIndex(existing.value).filter((item) => !item.id || !remove.has(item.id));
    existing.value = JSON.stringify(next);
  }

  async readDocumentIndex(context: Word.RequestContext): Promise<SemanticMetadata[]> {
    const props = context.document.properties.customProperties;
    props.load("items/key,value");
    await context.sync();
    const prop = props.items.find((item) => item.key === PROPERTY_NAME);
    return prop ? this.parseIndex(prop.value) : [];
  }

  private parseIndex(raw: string): SemanticMetadata[] {
    try {
      const parsed = JSON.parse(raw) as SemanticMetadata[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
}

export const metadataService = new MetadataService();
